import { supabase } from '../utils/supabase';

/**
 * Service for calculating matching scores between candidates and jobs
 */
export class MatchingScoreService {
  /**
   * Calculate comprehensive match score between a candidate and job
   * @param {Object} candidateProfile - Candidate's profile data
   * @param {Object} jobRequirements - Job requirements and details
   * @returns {Object} Match score and detailed breakdown
   */
  static calculateMatchScore(candidateProfile, jobRequirements) {
    const breakdown = {
      skillsScore: 0,
      experienceScore: 0,
      rateScore: 0
    };

    // 1. Skills matching (50% weight)
    const skillsWeight = 0.50;
    const requiredSkills = jobRequirements.skills_required || jobRequirements.required_skills || [];
    const candidateSkills = candidateProfile.skills || [];
    
    if (requiredSkills.length > 0) {
      const skillsMatchResult = this.calculateSkillsMatch(candidateSkills, requiredSkills);
      breakdown.skillsScore = skillsMatchResult.percentage;
    } else {
      breakdown.skillsScore = 50; // Neutral score if no skills specified
    }

    // 2. Experience matching (30% weight)
    const experienceWeight = 0.30;
    const requiredExperience = jobRequirements.min_experience_years || jobRequirements.experience_required || 0;
    const candidateExperience = candidateProfile.experience_years || 0;
    
    breakdown.experienceScore = this.calculateExperienceMatch(candidateExperience, requiredExperience);

    // 3. Rate compatibility (20% weight, includes currency conversion)
    const rateWeight = 0.20;
    const jobBudgetMin = jobRequirements.budget_min || jobRequirements.hourly_rate_min || jobRequirements.salary_min || 0;
    const jobBudgetMax = jobRequirements.budget_max || jobRequirements.hourly_rate_max || jobRequirements.salary_max || 0;
    const candidateRate = candidateProfile.hourly_rate || 0;
    const candidateCurrency = candidateProfile.preferences?.currency || 'USD';
    const jobCurrency = jobRequirements.currency || 'USD';
    
    breakdown.rateScore = this.calculateCurrencyAdjustedRateMatch(
      candidateRate,
      candidateCurrency,
      jobBudgetMin,
      jobBudgetMax,
      jobCurrency
    );

    // Calculate weighted final score (Total: 100%)
    const weights = {
      skills: skillsWeight,
      experience: experienceWeight,
      rate: rateWeight
    };

    const finalScore = Math.round(
      (breakdown.skillsScore * weights.skills) +
      (breakdown.experienceScore * weights.experience) +
      (breakdown.rateScore * weights.rate)
    );

    return {
      overallScore: Math.min(100, Math.max(0, finalScore)),
      breakdown,
      weights,
      matchFactors: this.generateMatchFactors(breakdown, weights)
    };
  }

  /**
   * Calculate skills matching percentage with enhanced logic
   * @param {Array} candidateSkills - Candidate's skills (can be strings or objects)
   * @param {Array} requiredSkills - Required skills for the job
   * @returns {Object} Skills match result
   */
  static calculateSkillsMatch(candidateSkills, requiredSkills) {
    if (!candidateSkills || !Array.isArray(candidateSkills) || candidateSkills.length === 0) {
      return { percentage: 0, matchingSkills: [], totalRequired: requiredSkills.length };
    }

    if (!requiredSkills || !Array.isArray(requiredSkills) || requiredSkills.length === 0) {
      return { percentage: 50, matchingSkills: [], totalRequired: 0 };
    }

    // Normalize skills to strings for comparison
    const normalizedCandidateSkills = candidateSkills.map(skill => {
      if (typeof skill === 'string') {
        return skill.toLowerCase().trim();
      } else if (typeof skill === 'object' && skill.name) {
        return skill.name.toLowerCase().trim();
      }
      return String(skill).toLowerCase().trim();
    });

    const normalizedRequiredSkills = requiredSkills.map(skill => 
      String(skill).toLowerCase().trim()
    );

    let exactMatches = 0;
    let partialMatches = 0;
    const matchingSkills = [];

    normalizedRequiredSkills.forEach(requiredSkill => {
      // Check for exact match
      const exactMatch = normalizedCandidateSkills.find(candidateSkill => 
        candidateSkill === requiredSkill
      );

      if (exactMatch) {
        exactMatches++;
        matchingSkills.push(requiredSkill);
        return;
      }

      // Check for partial match (contains)
      const partialMatch = normalizedCandidateSkills.find(candidateSkill => 
        candidateSkill.includes(requiredSkill) || 
        requiredSkill.includes(candidateSkill)
      );

      if (partialMatch) {
        partialMatches++;
        matchingSkills.push(requiredSkill);
      }
    });

    // Calculate percentage: exact matches get full points, partial matches get 0.5 points
    const totalMatches = exactMatches + (partialMatches * 0.5);
    const percentage = Math.round((totalMatches / normalizedRequiredSkills.length) * 100);

    return {
      percentage: Math.min(100, percentage),
      exactMatches,
      partialMatches,
      matchingSkills,
      totalRequired: normalizedRequiredSkills.length
    };
  }

  /**
   * Calculate experience compatibility score
   * @param {Number} candidateYears - Candidate's years of experience
   * @param {Number} requiredYears - Required years of experience
   * @returns {Number} Experience match score (0-100)
   */
  static calculateExperienceMatch(candidateYears, requiredYears) {
    if (!requiredYears || requiredYears === 0) {
      return 80; // Neutral-positive score if no experience required
    }

    if (!candidateYears || candidateYears === 0) {
      return requiredYears <= 1 ? 60 : 20; // Low score for no experience unless entry level
    }

    if (candidateYears >= requiredYears) {
      // Perfect match if meets requirements, bonus for exceeding (up to 100%)
      const bonus = Math.min(20, (candidateYears - requiredYears) * 2);
      return Math.min(100, 85 + bonus);
    } else {
      // Partial match based on how close they are
      const ratio = candidateYears / requiredYears;
      return Math.round(ratio * 75); // Max 75% if under-qualified
    }
  }

  /**
   * Calculate rate compatibility score
   * @param {Number} candidateRate - Candidate's hourly rate
   * @param {Number} jobBudgetMin - Job's minimum budget
   * @param {Number} jobBudgetMax - Job's maximum budget
   * @returns {Number} Rate compatibility score (0-100)
   */
  static calculateRateMatch(candidateRate, jobBudgetMin, jobBudgetMax) {
    if (!candidateRate || candidateRate === 0) {
      return 50; // Neutral score if rate not specified
    }

    if (!jobBudgetMin && !jobBudgetMax) {
      return 70; // Neutral-positive score if budget not specified
    }

    const budgetMin = jobBudgetMin || 0;
    const budgetMax = jobBudgetMax || jobBudgetMin || candidateRate * 2;

    // Perfect match if within budget range
    if (candidateRate >= budgetMin && candidateRate <= budgetMax) {
      return 100;
    }

    // Below minimum budget
    if (candidateRate < budgetMin) {
      const difference = budgetMin - candidateRate;
      const penalty = Math.min(30, (difference / budgetMin) * 40);
      return Math.max(40, 100 - penalty);
    }

    // Above maximum budget
    if (candidateRate > budgetMax) {
      const overBudget = (candidateRate - budgetMax) / budgetMax;
      const penalty = Math.min(50, overBudget * 60);
      return Math.max(20, 100 - penalty);
    }

    return 50;
  }

  /**
   * Calculate rating/quality score
   * @param {Number} rating - Candidate's average rating (0-5)
   * @param {Number} reviewCount - Number of reviews
   * @returns {Number} Rating score (0-100)
   */
  static calculateRatingScore(rating, reviewCount) {
    if (!rating || rating === 0) {
      return 50; // Neutral score for no ratings
    }

    // Base score from rating (0-5 scale to 0-100 scale)
    let score = (rating / 5) * 100;

    // Confidence bonus based on number of reviews
    if (reviewCount > 0) {
      const confidenceBonus = Math.min(15, Math.log10(reviewCount) * 10);
      score += confidenceBonus;
    } else {
      score *= 0.8; // Slight penalty for no reviews
    }

    return Math.round(Math.min(100, Math.max(0, score)));
  }

  /**
   * Calculate location compatibility score
   * @param {String} candidateLocation - Candidate's location
   * @param {String} jobLocation - Job location
   * @param {Boolean} isRemote - Whether job is remote
   * @returns {Number} Location compatibility score (0-100)
   */
  static calculateLocationMatch(candidateLocation, jobLocation, isRemote) {
    if (isRemote) {
      return 100; // Perfect match for remote jobs
    }

    if (!candidateLocation || !jobLocation) {
      return 60; // Neutral score if location not specified
    }

    const candidateLoc = candidateLocation.toLowerCase().trim();
    const jobLoc = jobLocation.toLowerCase().trim();

    // Exact match
    if (candidateLoc === jobLoc) {
      return 100;
    }

    // Check for city/state/country matches
    const candidateParts = candidateLoc.split(/[,\s]+/);
    const jobParts = jobLoc.split(/[,\s]+/);

    const commonParts = candidateParts.filter(part => 
      jobParts.some(jobPart => 
        part.includes(jobPart) || jobPart.includes(part)
      )
    );

    if (commonParts.length > 0) {
      return Math.min(90, 50 + (commonParts.length * 20));
    }

    return 30; // Low score for different locations
  }

  /**
   * Calculate timezone compatibility score based on working hour overlap
   * @param {String} candidateTimezone - Candidate's timezone (e.g., 'America/New_York')
   * @param {String} jobTimezone - Job/Employer's timezone
   * @param {Boolean} isRemote - Whether the job is remote
   * @returns {Number} Timezone compatibility score (0-100)
   */
  static calculateTimezoneMatch(candidateTimezone, jobTimezone, isRemote = false) {
    // If job is not remote, timezone matters less (assume in-person work)
    if (!isRemote) {
      return 80; // Neutral-high score for non-remote jobs
    }

    // If either timezone is not specified, return neutral score
    if (!candidateTimezone || !jobTimezone) {
      return 70;
    }

    // If timezones are the same, perfect match
    if (candidateTimezone === jobTimezone) {
      return 100;
    }

    try {
      // Calculate timezone offset difference in hours
      const candidateOffset = this.getTimezoneOffset(candidateTimezone);
      const jobOffset = this.getTimezoneOffset(jobTimezone);
      const hoursDifference = Math.abs(candidateOffset - jobOffset);

      // Score based on hour difference
      // 0-2 hours: Excellent (95-100)
      // 3-4 hours: Good (80-94)
      // 5-6 hours: Fair (60-79)
      // 7-8 hours: Poor (40-59)
      // 9+ hours: Very poor (20-39)
      if (hoursDifference <= 2) {
        return 100 - (hoursDifference * 2.5);
      } else if (hoursDifference <= 4) {
        return 94 - ((hoursDifference - 2) * 7);
      } else if (hoursDifference <= 6) {
        return 79 - ((hoursDifference - 4) * 10);
      } else if (hoursDifference <= 8) {
        return 59 - ((hoursDifference - 6) * 10);
      } else {
        return Math.max(20, 39 - ((hoursDifference - 8) * 2));
      }
    } catch (error) {
      console.error('Error calculating timezone match:', error);
      return 70; // Neutral score on error
    }
  }

  /**
   * Get timezone offset in hours from UTC
   * @param {String} timezone - Timezone string (e.g., 'America/New_York')
   * @returns {Number} Offset in hours from UTC
   */
  static getTimezoneOffset(timezone) {
    try {
      const now = new Date();
      const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
      const tzDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
      return (tzDate - utcDate) / (1000 * 60 * 60);
    } catch (error) {
      console.error(`Error getting offset for timezone ${timezone}:`, error);
      return 0;
    }
  }

  /**
   * Calculate language compatibility score
   * @param {Array|String} candidateLanguages - Candidate's spoken languages
   * @param {Array|String} requiredLanguages - Required languages for the job
   * @param {String} preferredLanguage - Candidate's preferred UI language
   * @returns {Number} Language compatibility score (0-100)
   */
  static calculateLanguageMatch(candidateLanguages, requiredLanguages = [], preferredLanguage = 'en') {
    // If no languages required, return high neutral score
    if (!requiredLanguages || requiredLanguages.length === 0) {
      return 85;
    }

    // Normalize inputs to arrays
    const candidateLangs = Array.isArray(candidateLanguages) 
      ? candidateLanguages.map(l => (typeof l === 'string' ? l : l.name || l.language)?.toLowerCase()?.trim())
      : [candidateLanguages?.toLowerCase()?.trim()].filter(Boolean);

    const requiredLangs = Array.isArray(requiredLanguages)
      ? requiredLanguages.map(l => l?.toLowerCase()?.trim())
      : [requiredLanguages?.toLowerCase()?.trim()].filter(Boolean);

    // Add preferred language to candidate languages if not already present
    if (preferredLanguage && !candidateLangs.includes(preferredLanguage.toLowerCase())) {
      candidateLangs.push(preferredLanguage.toLowerCase());
    }

    // If candidate has no languages specified, return low score
    if (candidateLangs.length === 0) {
      return 30;
    }

    // Calculate matches
    let matchedCount = 0;
    let partialMatchCount = 0;

    for (const reqLang of requiredLangs) {
      if (candidateLangs.includes(reqLang)) {
        matchedCount++;
      } else {
        // Check for partial matches (e.g., 'en' matches 'english')
        const partialMatch = candidateLangs.some(candLang => 
          candLang.includes(reqLang.substring(0, 2)) || 
          reqLang.includes(candLang.substring(0, 2))
        );
        if (partialMatch) {
          partialMatchCount++;
        }
      }
    }

    // Calculate score: 100% for all matches, partial credit for partial matches
    const matchPercentage = (matchedCount + (partialMatchCount * 0.5)) / requiredLangs.length;
    return Math.round(Math.min(100, matchPercentage * 100));
  }

  /**
   * Calculate currency compatibility score for rate matching
   * @param {Number} candidateRate - Candidate's hourly rate
   * @param {String} candidateCurrency - Candidate's preferred currency
   * @param {Number} jobBudgetMin - Job's minimum budget
   * @param {Number} jobBudgetMax - Job's maximum budget
   * @param {String} jobCurrency - Job's budget currency
   * @returns {Number} Currency-adjusted rate compatibility score (0-100)
   */
  static calculateCurrencyAdjustedRateMatch(
    candidateRate,
    candidateCurrency = 'USD',
    jobBudgetMin,
    jobBudgetMax,
    jobCurrency = 'USD'
  ) {
    // If currencies match, use standard rate calculation
    if (candidateCurrency === jobCurrency) {
      return this.calculateRateMatch(candidateRate, jobBudgetMin, jobBudgetMax);
    }

    try {
      // Convert candidate rate to job currency
      const convertedRate = this.convertCurrency(candidateRate, candidateCurrency, jobCurrency);
      return this.calculateRateMatch(convertedRate, jobBudgetMin, jobBudgetMax);
    } catch (error) {
      console.error('Error converting currency:', error);
      // Fallback to standard calculation with a slight penalty
      const score = this.calculateRateMatch(candidateRate, jobBudgetMin, jobBudgetMax);
      return Math.max(0, score - 10); // 10 point penalty for currency mismatch
    }
  }

  /**
   * Convert currency (simplified version - in production, use live exchange rates)
   * @param {Number} amount - Amount to convert
   * @param {String} fromCurrency - Source currency
   * @param {String} toCurrency - Target currency
   * @returns {Number} Converted amount
   */
  static convertCurrency(amount, fromCurrency, toCurrency) {
    // Simplified exchange rates (as of example - should use live API in production)
    const exchangeRates = {
      'USD': 1.0,
      'EUR': 0.92,
      'GBP': 0.79,
      'CAD': 1.36,
      'AUD': 1.52,
      'JPY': 149.50,
      'PHP': 56.25,
      'INR': 83.12
    };

    const fromRate = exchangeRates[fromCurrency] || 1;
    const toRate = exchangeRates[toCurrency] || 1;

    // Convert to USD first, then to target currency
    const usdAmount = amount / fromRate;
    return usdAmount * toRate;
  }

  /**
   * Generate match factors explanation
   * @param {Object} breakdown - Score breakdown
   * @param {Object} _weights - Scoring weights (unused but kept for future extensibility)
   * @returns {Array} Array of match factors
   */
  static generateMatchFactors(breakdown, _weights) {
    const factors = [];

    if (breakdown.skillsScore >= 80) {
      factors.push('Excellent skills match');
    } else if (breakdown.skillsScore >= 60) {
      factors.push('Good skills match');
    } else if (breakdown.skillsScore < 40) {
      factors.push('Limited skills match');
    }

    if (breakdown.experienceScore >= 85) {
      factors.push('Strong experience match');
    } else if (breakdown.experienceScore < 50) {
      factors.push('Experience gap identified');
    }

    if (breakdown.rateScore >= 90) {
      factors.push('Rate within budget');
    } else if (breakdown.rateScore < 40) {
      factors.push('Rate compatibility concerns');
    }

    if (breakdown.ratingScore >= 80) {
      factors.push('Highly rated candidate');
    }

    // Timezone compatibility factors
    if (breakdown.timezoneScore >= 90) {
      factors.push('Excellent timezone compatibility');
    } else if (breakdown.timezoneScore >= 70) {
      factors.push('Good timezone overlap');
    } else if (breakdown.timezoneScore < 50) {
      factors.push('Timezone challenges may exist');
    }

    // Language compatibility factors
    if (breakdown.languageScore >= 90) {
      factors.push('Strong language match');
    } else if (breakdown.languageScore < 60) {
      factors.push('Language compatibility concerns');
    }

    return factors;
  }

  /**
   * Calculate and update match score for a job application
   * @param {String} applicationId - Application ID
   * @param {String} jobId - Job ID
   * @param {String} candidateId - Candidate ID
   * @returns {Promise<Object>} Updated match score result
   */
  static async calculateAndUpdateApplicationScore(applicationId, jobId, candidateId) {
    try {
      // Get job details
      const { data: jobData, error: jobError } = await supabase
        .from('jobs')
        .select(`
          id,
          title,
          skills_required,
          min_experience_years,
          budget_min,
          budget_max,
          hourly_rate_min,
          hourly_rate_max,
          location,
          is_remote
        `)
        .eq('id', jobId)
        .limit(1);

      if (jobError) throw jobError;
      
      const job = jobData && jobData.length > 0 ? jobData[0] : null;
      if (!job) {
        throw new Error(`Job not found for ID: ${jobId}`);
      }

      // Get candidate profile with analytics data
      const { data: candidateData, error: candidateError } = await supabase
        .from('user_profiles')
        .select(`
          id,
          skills,
          experience_years,
          hourly_rate,
          location,
          user_analytics (
            average_rating,
            total_earnings
          )
        `)
        .eq('id', candidateId)
        .limit(1);

      if (candidateError) throw candidateError;
      
      const candidate = candidateData && candidateData.length > 0 ? candidateData[0] : null;
      if (!candidate) {
        throw new Error(`Candidate profile not found for ID: ${candidateId}`);
      }

      // Calculate match score
      const matchResult = this.calculateMatchScore(candidate, job);

      // Update application with match score
      // Use a more permissive approach that doesn't require .single()
      const { data: updatedApplications, error: updateError } = await supabase
        .from('job_applications')
        .update({
          match_percentage: matchResult.overallScore,
          match_breakdown: matchResult.breakdown,
          match_factors: matchResult.matchFactors,
          updated_at: new Date().toISOString()
        })
        .eq('id', applicationId)
        .select();

      // Handle RLS policy rejections gracefully
      if (updateError) {
        console.warn('Match score update failed due to RLS policy:', updateError);
        // Return success even if we can't update the scores due to permissions
        return {
          success: true,
          data: {
            applicationId,
            matchScore: matchResult.overallScore,
            breakdown: matchResult.breakdown,
            factors: matchResult.matchFactors,
            application: null, // Couldn't update due to permissions
            note: 'Match calculated but not saved due to permissions'
          }
        };
      }

      const updatedApplication = updatedApplications && updatedApplications.length > 0 ? updatedApplications[0] : null;

      return {
        success: true,
        data: {
          applicationId,
          matchScore: matchResult.overallScore,
          breakdown: matchResult.breakdown,
          factors: matchResult.matchFactors,
          application: updatedApplication
        }
      };

    } catch (error) {
      console.error('Error calculating application match score:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Batch update match scores for all applications of a job
   * @param {String} jobId - Job ID
   * @returns {Promise<Object>} Batch update result
   */
  static async updateJobApplicationScores(jobId) {
    try {
      // Get all applications for this job
      const { data: applications, error: appsError } = await supabase
        .from('job_applications')
        .select(`
          id,
          applicant_id,
          applicant:user_profiles!job_applications_applicant_id_fkey(*)
        `)
        .eq('job_id', jobId);

      if (appsError) throw appsError;

      // Get job details
      const { data: job, error: jobError } = await supabase
        .from('jobs')
        .select('*')
        .eq('id', jobId)
        .single();

      if (jobError) throw jobError;

      // Calculate and update match scores for all applications
      const updatePromises = applications.map(async (app) => {
        const matchResult = this.calculateMatchScore(app.applicant, job);
        
        return supabase
          .from('job_applications')
          .update({
            match_percentage: matchResult.overallScore,
            match_breakdown: matchResult.breakdown,
            match_factors: matchResult.matchFactors,
            updated_at: new Date().toISOString()
          })
          .eq('id', app.id);
      });

      const results = await Promise.all(updatePromises);
      const successful = results.filter(result => !result.error).length;
      const failed = results.filter(result => result.error).length;

      return {
        success: true,
        data: {
          totalApplications: applications.length,
          successful,
          failed,
          results
        }
      };

    } catch (error) {
      console.error('Error batch updating application match scores:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

export default MatchingScoreService;
